import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  modelName?: string;
}

export function ChatInput({ value, onChange, onSubmit, isLoading, modelName }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 20;
    const maxHeight = lineHeight * 4 + 16;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: value triggers resize when content changes
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        onSubmit();
      }
    }
  };

  return (
    <div className="px-3 py-3">
      <div className="flex items-end gap-2 rounded-2xl border border-border bg-card/90 p-2 shadow-lg backdrop-blur focus-within:ring-1 focus-within:ring-primary">
        <label htmlFor="creation-chat-input" className="sr-only">
          Message Atlas
        </label>
        <textarea
          id="creation-chat-input"
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isLoading ? "Atlas is thinking…" : "Reply to Atlas…"}
          disabled={isLoading}
          rows={1}
          className="min-h-[40px] max-h-[120px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
        />
        <Button
          size="icon"
          className="h-9 w-9 shrink-0 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={onSubmit}
          disabled={!value.trim() || isLoading}
          aria-label="Send"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      </div>
      {modelName && (
        <p className="mt-1 text-center text-[10px] text-muted-foreground/60">{modelName}</p>
      )}
    </div>
  );
}
